// The Revenue Curve Forecast tab's month math, pulled out of
// costCurveForecast.js so another screen can read the same two curves the
// tab charts (cumulative Revenue and cumulative Total EAC) without rendering
// it. Everything here is pure: the tab's component keeps its edits, filters
// and persistence and calls these with the data it already holds.
//
// Per month the tab sums six cost rows — Labor and Equipment off the Burn
// Tool's weeks, the four third-party rows off posted actuals plus the ETC the
// user spread on the tab — and sets Revenue to what the GL posted (4xxxx
// lines, else AR billings) for closed months and to cost grossed up by the
// job's EAC margin for the forecast month onward.

import moment from 'moment';
import { pNum } from '../db/company';
import { buildEtcLedger } from './etcLedger';
import { SERIES, SERIES_OTHER } from '../styles/chartColors';

export const TAB = 'Revenue Curve';

// Reserved Forecast_Row_ID (no month prefix, so the cell readers skip it)
// holding the user-chosen end month of the chart/table window as YYYYMM.
export const HORIZON_ROW_ID = 'horizonEnd';

export const LABOR_RANGE = ['51000', '51799'];
export const EQUIP_RANGE = ['52100', '52199'];

// The Burn Tool saves its labor/equipment estimate under a phased tab on phased
// jobs and a plain tab on regular jobs. Only ever one or the other holds data
// for a given job, so both are read and the phased figure wins for any week
// that has one (covers a job migrated mid-forecast).
export const BURN_TABS = {
    labor: { phased: 'Burn Tool Labor Phased', regular: 'Burn Tool Labor' },
    equipment: { phased: 'Burn Tool Equipment Phased', regular: 'Burn Tool Equipment' },
};

// Revenue (40000-49999) posts as negative Job_Cost in Job Cost History —
// negate it so the Revenue row reads as a positive dollar amount.
export const REVENUE_RANGE = ['40000', '49999'];
// Over/Under Billings — accrues at month-end and reverses on the 1st, so the
// open forecast month only ever carries the unmatched reversal.
export const OU_BILLINGS_TYPE = '43000';

// `range` drives the actuals lookup and only the editable rows use it — Labor
// and Equipment read their curve from the Burn Tool instead, so they carry
// `curveRange` for grouping and filtering. `budgetRanges` is wider: together
// the six rows cover every cost type the day-zero budget buckets do. Fuel
// (52300) and Repairs (52400) fold into Rentals.
export const ROWS = [
    { key: 'labor', label: 'Labor', costType: '51100', color: SERIES[0], editable: false, source: 'burn', curveRange: LABOR_RANGE, budgetRanges: [['51000', '51999']] },
    { key: 'equipment', label: 'Equipment', costType: '52100', color: SERIES[1], editable: false, source: 'burn', curveRange: EQUIP_RANGE, budgetRanges: [['52000', '52199'], ['52500', '52999']] },
    { key: 'rentals', label: 'Rentals, Fuel, & Repairs', costType: '52200', color: SERIES[2], editable: true, range: ['52200', '52499'], budgetRanges: [['52200', '52499']], etcTab: 'Rentals' },
    { key: 'materials', label: 'Materials', costType: '53100', color: SERIES[3], editable: true, range: ['53000', '53999'], budgetRanges: [['53000', '53999']], etcTab: 'Materials' },
    { key: 'subs', label: 'Subcontractors', costType: '54100', color: SERIES[4], editable: true, range: ['54000', '54999'], budgetRanges: [['54000', '54999']], etcTab: 'Subcontractors' },
    { key: 'other', label: 'Other', costType: '55100', color: SERIES_OTHER, editable: true, range: ['55000', '58000'], budgetRanges: [['55000', '58000']], etcTab: 'Other' },
];
// Fuel and Repairs ETC is entered on the Other tab in the DB, but rolls into
// Rentals here — moved over in etcPoolByRowOf.
export const FUEL_REPAIRS_TYPES = ['52300', '52400'];

// Row id shapes across the Burn Tool tabs: phased "<phaseCode>=<week>", regular
// labor "<week>*labor", regular equipment "<week>". Pull the ISO week-start out of any.
export const burnRowWeek = (id) => {
    const s = String(id || '');
    const wk = s.includes('=') ? s.split('=')[1] : s.split('*')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(wk) ? wk : null;
};
// Dollar estimate for one Burn Tool week row: re-derive from the hours / rate /
// crew-size resolved onto the row rather than trusting a stale Value_Amount,
// falling back to Value_Amount only for legacy rows that never stored the parts.
export const burnRowEst = (d) => {
    if (d?.Rate != null || d?.Number_Hours != null || d?.Number_Units != null) {
        // No burn rate entered means zero est hours, even if a crew size or
        // hours-per-week value is still sitting on the row.
        const hours = pNum(d.Rate) > 0 && d.Number_Hours ? pNum(d.Number_Hours) : 0;
        return hours * pNum(d.Rate) * pNum(d.Number_Units);
    }
    return pNum(d?.Value_Amount);
};

// A cell is identified by month and cost type.
export const cellId = (monthKey, costType) => `${monthKey}*${costType}`;

// A Burn Tool week holds one amount for a Monday-to-Saturday work week. When
// that week crosses a month end, the cost belongs to whichever month each work
// day falls in — dropping the whole week on the month its Monday sits in moves
// real money between periods (the week of Mon Aug 31 is five sixths September).
export const WORK_DAYS = 6;
export const spreadWorkWeek = (weekStart, amount, into) => {
    const days = {};
    for (let i = 0; i < WORK_DAYS; i++) {
        const mk = moment(weekStart).add(i, 'days').format('YYYY-MM');
        days[mk] = (days[mk] || 0) + 1;
    }
    Object.entries(days).forEach(([mk, n]) => {
        into[mk] = (into[mk] || 0) + (amount * n) / WORK_DAYS;
    });
};

const monthOf = (d) => ({ key: d.format('YYYY-MM'), label: d.format("MMM 'YY"), start: d.clone() });

/** The typed end month of the window (YYYYMM under HORIZON_ROW_ID), or null. */
export const overrideMonthOf = (userData) => {
    let v = 0;
    (userData || []).forEach((d) => {
        if (d?.Forecast_Tab === TAB && String(d.Forecast_Row_ID) === HORIZON_ROW_ID) v = pNum(d.Value_Amount);
    });
    if (!v) return null;
    const m = moment(`${Math.floor(v / 100)}-${String(v % 100).padStart(2, '0')}-01`, 'YYYY-MM-DD');
    return m.isValid() ? m.startOf('month') : null;
};

/**
 * Every month the tab can show: from the earliest of the job start, cost
 * history, saved spreads and Burn Tool weeks to the latest of those padded
 * out past the forecast month.
 */
export const monthsOf = ({ selectedJobData, completionDateForecast, jobCostDetail, userData, extraMonths = 0, overrideMonth, currentMonth }) => {
    const dates = [];
    const start = selectedJobData?.Planned_Start_Date;
    const finish = completionDateForecast || selectedJobData?.Planned_Complete_Date;
    if (start) dates.push(moment(start));
    if (finish) dates.push(moment(finish));

    (jobCostDetail || []).forEach((r) => { if (r?.Consolidated_Date) dates.push(moment(r.Consolidated_Date)); });
    (userData || []).forEach((d) => {
        if (d?.Forecast_Tab === TAB) {
            const mk = String(d.Forecast_Row_ID || '').split('*')[0];
            if (/^\d{4}-\d{2}$/.test(mk)) dates.push(moment(mk + '-01'));
        }
        // Burn Tool weeks too, out to the Saturday: a week that starts in
        // the last month of the range spreads into the next one, and cost
        // landing in a month with no column would go unreported.
        if (/^Burn Tool (Labor|Equipment)( Phased)?$/.test(d?.Forecast_Tab || '')) {
            const wk = burnRowWeek(d.Forecast_Row_ID);
            if (wk) dates.push(moment(wk).add(WORK_DAYS - 1, 'days'));
        }
    });

    let min, max;
    dates.forEach((d) => {
        if (!d.isValid()) return;
        if (!min || d.isBefore(min)) min = d.clone();
        if (!max || d.isAfter(max)) max = d.clone();
    });
    if (!min || !max) { min = currentMonth.clone(); max = currentMonth.clone().add(11, 'months'); }

    const out = [];
    const cur = min.clone().startOf('month');
    // Always run far enough past the forecast month to offer the months the
    // window can show, even on a job whose last known date is behind it.
    const padded = max.clone().startOf('month').add(5 + extraMonths, 'months');
    const needed = currentMonth.clone().add(4 + extraMonths, 'months');
    let end = padded.isAfter(needed) ? padded : needed;
    // A typed end month past the data-driven range still needs columns to
    // exist out to it, or the horizon field couldn't actually extend the view.
    if (overrideMonth && overrideMonth.isAfter(end)) end = overrideMonth;
    let guard = 0;
    while (cur.isSameOrBefore(end) && guard < 360) {
        out.push(monthOf(cur));
        cur.add(1, 'month');
        guard++;
    }
    return out;
};

/**
 * Default horizon is Complete Date - Forecast plus 5 months; a typed end month
 * wins outright. "+ 5 mo" (extraMonths) pushes this horizon further.
 */
export const horizonEndOf = ({ completionDateForecast, selectedJobData, extraMonths = 0, overrideMonth, currentMonth }) => {
    if (overrideMonth) return overrideMonth;
    const anchor = completionDateForecast || selectedJobData?.Planned_Complete_Date;
    const base = (anchor ? moment(anchor) : currentMonth.clone()).startOf('month');
    const padded = base.clone().add(5 + extraMonths, 'months');
    const needed = currentMonth.clone().add(4 + extraMonths, 'months');
    return padded.isAfter(needed) ? padded : needed;
};

/**
 * Posted revenue by month. Cost-plus / T&M jobs post revenue as GL lines
 * (4xxxx) in Job Cost History; fixed-price jobs recognise it only through AR
 * billings, so fall back to billed amount by GL month when the job has none.
 */
export const revenueMonthlyFrom = (costDetail, jobBillings, currentMonth) => {
    const out = {};
    const curKey = currentMonth.format('YYYY-MM');
    (costDetail || []).forEach((r) => {
        const ct = r?.Cost_Type_Code != null ? String(r.Cost_Type_Code) : '';
        if (ct >= REVENUE_RANGE[0] && ct <= REVENUE_RANGE[1]) {
            // Same +12h guard as the burn/actuals reads: a date stored as UTC
            // midnight otherwise falls back a calendar day in any timezone
            // behind UTC, which can cross a month boundary. Over/Under
            // Billings (43000) posts as a same-amount accrual and reversal on
            // consecutive midnights around month-end, so that shift silently
            // cancels the pair out of every month.
            const mk = moment(r.Consolidated_Date).add(12, 'hours').format('YYYY-MM');
            // The forecast month can only ever hold the *unmatched* half of
            // that pair (the reversal on the 1st; the accrual books at
            // month-end, after the version snapshot), so O/U Billings is
            // dropped from the open month entirely.
            if (ct === OU_BILLINGS_TYPE && mk === curKey) return;
            out[mk] = (out[mk] || 0) - (Number(r.Job_Cost) || 0);
        }
    });
    if (Object.keys(out).length) return out;

    (jobBillings || []).forEach((b) => {
        if (!b?.GL_Date) return;
        const mk = moment(b.GL_Date).add(12, 'hours').format('YYYY-MM');
        out[mk] = (out[mk] || 0) + (pNum(b.Invoice_Amount) || 0);
    });
    return out;
};

/**
 * Labor / Equipment monthly curve from the Burn Tool: { labor: { actual, est },
 * equipment: { actual, est } }, each keyed by YYYY-MM. `burnCutoff` is the
 * date after which a week's estimate stands in for its (still landing) actuals.
 */
export const burnMonthlyFrom = (costDetail, userData, burnCutoff) => {
    const build = (range, burnKey) => {
        const tabs = BURN_TABS[burnKey];
        // Actuals are kept per week *and* per month within that week: the
        // week decides whether the estimate or the actual applies, but the
        // cost itself carries a real date, so it lands in the month it
        // posted in rather than the month its Monday happens to sit in.
        const actualByWeek = {};
        (costDetail || []).forEach((r) => {
            const ct = r?.Cost_Type_Code != null ? String(r.Cost_Type_Code) : '';
            if (ct >= range[0] && ct <= range[1]) {
                const d = moment(r.Consolidated_Date).add(12, 'hours');
                const wk = d.clone().startOf('isoWeek').format('YYYY-MM-DD');
                const mk = d.format('YYYY-MM');
                const bucket = actualByWeek[wk] || (actualByWeek[wk] = {});
                bucket[mk] = (bucket[mk] || 0) + (Number(r.Job_Cost) || 0);
            }
        });
        // One row per week (regular job) or per phase code per week (phased),
        // re-derived from hours/rate/crew-size. A later duplicate row for the
        // same id (an edit) overwrites the earlier one.
        const phasedById = {};
        const regularById = {};
        (userData || []).forEach((d) => {
            if (d?.Forecast_Tab === tabs.phased) phasedById[d.Forecast_Row_ID] = d;
            else if (d?.Forecast_Tab === tabs.regular) regularById[d.Forecast_Row_ID] = d;
        });
        const weekEst = (byId) => {
            const out = {};
            Object.entries(byId).forEach(([id, d]) => {
                const wk = burnRowWeek(id);
                if (!wk) return;
                const val = burnRowEst(d);
                if (!val) return;
                out[wk] = (out[wk] || 0) + val;
            });
            return out;
        };
        // A job runs one Burn Tool or the other; if it was migrated, the
        // phased figure is the live one, so it wins per week.
        const estByWeek = { ...weekEst(regularById), ...weekEst(phasedById) };
        // Kept apart rather than summed: a month can hold closed weeks and
        // open ones at the same time, and only the closed half counts as spent.
        const actual = {};
        const est = {};
        const addAll = (into, byMonth) => Object.entries(byMonth || {})
            .forEach(([mk, amt]) => { into[mk] = (into[mk] || 0) + amt; });

        new Set([...Object.keys(actualByWeek), ...Object.keys(estByWeek)]).forEach((wk) => {
            if (!moment(wk).isAfter(burnCutoff)) {
                if (actualByWeek[wk]) {
                    addAll(actual, actualByWeek[wk]);
                } else if (estByWeek[wk] != null) {
                    // Nothing has posted yet for this week even though it's at
                    // or before the cutoff — keep the Burn Tool's own estimate
                    // as ETC instead of dropping it.
                    spreadWorkWeek(wk, estByWeek[wk], est);
                }
                return;
            }
            // A forecast week with nothing entered in the Burn Tool falls
            // back to whatever posted that week, which already carries its
            // own dates; a real estimate covers the work week and is split
            // across the months those days fall in.
            if (estByWeek[wk] != null) spreadWorkWeek(wk, estByWeek[wk], est);
            else addAll(est, actualByWeek[wk]);
        });
        return { actual, est };
    };
    return {
        labor: build(LABOR_RANGE, 'labor'),
        equipment: build(EQUIP_RANGE, 'equipment'),
    };
};

/** Posted actuals by month for the editable (third-party) rows: { rowKey: { YYYY-MM: $ } }. */
export const actualsMonthlyFrom = (costDetail) => {
    const out = {};
    ROWS.filter((r) => r.editable).forEach((r) => (out[r.key] = {}));
    (costDetail || []).forEach((row) => {
        const ct = row?.Cost_Type_Code != null ? String(row.Cost_Type_Code) : '';
        ROWS.forEach((r) => {
            if (r.range && ct >= r.range[0] && ct <= r.range[1]) {
                // Same +12h guard as the burn and revenue reads: the date
                // arrives as UTC midnight, so without it a cost dated the 1st
                // lands in the month before in any timezone behind UTC.
                const mk = moment(row.Consolidated_Date).add(12, 'hours').format('YYYY-MM');
                out[r.key][mk] = (out[r.key][mk] || 0) + (Number(row.Job_Cost) || 0);
            }
        });
    });
    return out;
};

/** The tab's saved cells by Forecast_Row_ID: { id: { amount, guid } }. */
export const savedByRowIdOf = (userData) => {
    const map = {};
    (userData || []).forEach((d) => {
        if (d?.Forecast_Tab !== TAB) return;
        const id = String(d.Forecast_Row_ID || '');
        map[id] = { amount: pNum(d.Value_Amount), guid: d.Forecast_User_Data_GUID || '' };
    });
    return map;
};

// The tab used to let a month's ETC be split across a drill path; some saved
// rows still carry that `month*costType*node` id shape. Grouped by their
// `month*costType` base so a category still totals them.
export const nodeIdsByBaseOf = (savedByRowId) => {
    const m = {};
    Object.keys(savedByRowId || {}).forEach((id) => {
        const p = id.split('*');
        if (p.length > 2) {
            const base = p[0] + '*' + p[1];
            (m[base] = m[base] || []).push(id);
        }
    });
    return m;
};

/**
 * ETC already entered on the third-party pages, per row key (null for the
 * Burn Tool rows). Fuel and Repairs move from Other to Rentals.
 */
export const etcPoolByRowOf = (userData) => {
    const out = {};
    const drillCols = ['Phase_Code', 'Supplier_Name', 'PO_Number'];
    const entriesByTab = {};
    const entriesFor = (tab) => (entriesByTab[tab] || (
        entriesByTab[tab] = buildEtcLedger(userData, tab, drillCols, {}).allEntries || []
    ));
    ROWS.forEach((r) => {
        if (!r.etcTab) { out[r.key] = null; return; }
        out[r.key] = entriesFor(r.etcTab).reduce((s, e) => s + (e.amount || 0), 0);
    });
    const fuelRepairsEtc = entriesFor('Other')
        .filter((e) => FUEL_REPAIRS_TYPES.includes(e.costType))
        .reduce((s, e) => s + (e.amount || 0), 0);
    if (out.other != null) out.other -= fuelRepairsEtc;
    if (out.rentals != null) out.rentals += fuelRepairsEtc;
    return out;
};

/**
 * The job's own EAC margin — the same JP% the Overview tab's Job Profit card
 * shows. Revenue is contract + approved COs; cost is the EAC across the six
 * buckets plus contingency. Null when there is no revenue or the margin is at
 * or over 100% (grossing cost up by it would divide by zero or flip sign).
 */
export const jobProfitPctOf = (smBudgets, smCosts) => {
    const revenue = (smBudgets?.contractValuesRevised || 0) + (smBudgets?.changeOrderRevenue || 0);
    if (!revenue) return null;
    const costEAC = (smCosts?.labor || 0) + (smCosts?.equipment || 0) + (smCosts?.materials || 0) +
        (smCosts?.subs || 0) + (smCosts?.other || 0) + (smCosts?.contingency || 0);
    const pct = (revenue - costEAC) / revenue;
    return pct < 1 ? pct : null;
};

/**
 * The two curves of the Revenue Curve Forecast tab, whole job, as saved (no
 * unsaved edits, no vendor/phase filter): one entry per visible month with
 * that month's cost (the six rows summed) and revenue.
 *
 * "Now" is the version's Forecast Date, not the wall clock — a forecast dated
 * in August is still the August period when it's opened in September.
 */
export const revenueCurveModel = ({ userData, jobCostDetail, jobBillings, selectedJobData, completionDateForecast, versionInfo, smBudgets, smCosts, today } = {}) => {
    const forecastDate = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime) : moment(today || undefined);
    const currentMonth = forecastDate.clone().startOf('month');
    const isPast = (m) => m.start.isBefore(currentMonth);
    // Where the Burn Tool stops trusting actuals and starts trusting the
    // estimate: a week is forecast when it starts after the snapshot's prior week.
    const burnCutoff = forecastDate.clone().add(-7, 'days');

    const overrideMonth = overrideMonthOf(userData);
    const months = monthsOf({ selectedJobData, completionDateForecast, jobCostDetail, userData, overrideMonth, currentMonth });
    const horizonEnd = horizonEndOf({ completionDateForecast, selectedJobData, overrideMonth, currentMonth });
    const visible = months.filter((m) => isPast(m) || m.start.isSameOrBefore(horizonEnd));

    const burn = burnMonthlyFrom(jobCostDetail, userData, burnCutoff);
    const actuals = actualsMonthlyFrom(jobCostDetail);
    const saved = savedByRowIdOf(userData);
    const nodeIds = nodeIdsByBaseOf(saved);
    const savedAmt = (id) => saved[id]?.amount || 0;
    const categoryEtc = (row, m) => {
        const base = cellId(m.key, row.costType);
        return savedAmt(base) + (nodeIds[base] || []).reduce((s, id) => s + savedAmt(id), 0);
    };
    const actual = (row, m) => (row.editable
        ? (actuals[row.key] && actuals[row.key][m.key]) || 0
        : (burn[row.key] && burn[row.key].actual[m.key]) || 0);
    const etc = (row, m) => {
        if (!row.editable) return (burn[row.key] && burn[row.key].est[m.key]) || 0;
        return isPast(m) ? 0 : categoryEtc(row, m);
    };
    const monthCost = (m) => ROWS.reduce((s, row) => s + actual(row, m) + etc(row, m), 0);

    const revenueMonthly = revenueMonthlyFrom(jobCostDetail, jobBillings, currentMonth);
    const jobProfitPct = jobProfitPctOf(smBudgets, smCosts);
    // Closed months report the revenue that posted; the forecast month and
    // every month after earn their whole cost at JP% (Revenue = Cost / (1 - JP%)).
    const monthRevenue = (m, cost) => (isPast(m) || jobProfitPct == null ? (revenueMonthly[m.key] || 0) : cost / (1 - jobProfitPct));

    let dataDate = '';
    (jobCostDetail || []).forEach((r) => {
        if (!r?.Consolidated_Date) return;
        const d = moment(r.Consolidated_Date).add(12, 'hours');
        if (d.isValid()) { const s = d.format('YYYY-MM-DD'); if (s > dataDate) dataDate = s; }
    });

    const rows = visible.map((m) => {
        const cost = monthCost(m);
        const revenue = monthRevenue(m, cost);
        return { key: m.key, label: m.label, start: m.start, past: isPast(m), current: m.start.isSame(currentMonth), cost, revenue };
    });
    return { months: rows, currentMonth, horizonEnd, jobProfitPct, dataDate, revenueSource: Object.keys(revenueMonthly).length ? (jobCostDetail || []).some((r) => { const ct = r?.Cost_Type_Code != null ? String(r.Cost_Type_Code) : ''; return ct >= REVENUE_RANGE[0] && ct <= REVENUE_RANGE[1]; }) ? 'gl' : 'billings' : 'none' };
};
